/**
 * RB-33 / D70 — the three dialog widths. There is no default on purpose: every
 * `DialogContent` / `AlertDialogContent` / `ResponsiveDialog` states its tier, so "inherited
 * `max-w-lg`" can no longer happen by omission.
 *
 * - `confirm`   28 rem — a yes/no, or a one-field prompt.
 * - `form`      48 rem — an ordinary form; fields go two to a row (`DialogFieldGrid`).
 * - `workspace` 72 rem, never more than 80 vw — editors, composers, and the two-pane creators.
 *
 * Below `sm` the cap stays where it was (`confirm` keeps 24 rem), so a dialog that is still a
 * centred dialog on a phone looks exactly as it did. Kept apart from `dialog.tsx` so that file
 * exports components only (fast refresh).
 */
export type DialogSize = 'confirm' | 'form' | 'workspace'

export const DIALOG_SIZE_CLASS: Record<DialogSize, string> = {
  // A confirm never outgrows the viewport: its content scrolls instead (RB-37 / row 87 — a bulk
  // delete of six clients pushed the title and both buttons off a 560 px screen). A confirm whose
  // body can grow (a list of names) scrolls just that body and keeps the title and buttons pinned
  // — see DestructiveConfirmDialog; this is the safety net for every other confirm.
  confirm: 'max-w-sm sm:max-w-[28rem] max-h-[90dvh] overflow-y-auto',
  form: 'max-w-lg sm:max-w-[48rem]',
  workspace: 'max-w-lg sm:w-[80vw] sm:max-w-[72rem]',
}

/**
 * `panes` is the D70 two-pane frame: one header, a left CHOICE pane beside a right CONSEQUENCE
 * pane, one footer — see `DialogPanesHeader` / `DialogPanes` / `DialogChoicePane` /
 * `DialogConsequencePane` / `DialogPanesFooter` in `dialog.tsx`. The panes stack (choice first)
 * below `md`.
 */
export type DialogLayout = 'stack' | 'panes'
